import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  primaryColor: string | null;
  logoUrl: string | null;
  groupName: string | null;
}

const DEFAULT_COLOR = '#1d4ed8';
const DEFAULT_COLOR_RGB = '29,78,216';
const DEFAULT_LOGO = '/rs-mandato-logo.png';

const ThemeContext = createContext<ThemeContextType>({
  primaryColor: null,
  logoUrl: null,
  groupName: null,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData } = useAuth();

  const primaryColor = userData?.grupo_cor_primaria ?? null;
  const primaryColorRgb = userData?.grupo_cor_primaria_rgb ?? null;
  const logoUrl = userData?.grupo_logo_url ?? null;
  const groupName = userData?.grupo_nome ?? null;

  useEffect(() => {
    const root = document.documentElement;

    if (primaryColor && primaryColorRgb) {
      root.style.setProperty('--accent-dark', primaryColor);
      root.style.setProperty('--accent-rgb', primaryColorRgb);
      root.style.setProperty('--accent-subtle', `rgba(${primaryColorRgb}, 0.08)`);
    } else {
      // Restore defaults
      root.style.setProperty('--accent-dark', DEFAULT_COLOR);
      root.style.setProperty('--accent-rgb', DEFAULT_COLOR_RGB);
      root.style.setProperty('--accent-subtle', `rgba(${DEFAULT_COLOR_RGB}, 0.08)`);
    }
  }, [primaryColor, primaryColorRgb]);

  useEffect(() => {
    if (groupName) {
      document.title = `RS Mandato - ${groupName}`;
    } else {
      document.title = 'RS Mandato';
    }
  }, [groupName]);

  return (
    <ThemeContext.Provider value={{ primaryColor, logoUrl, groupName }}>
      {children}
    </ThemeContext.Provider>
  );
};

/** Returns the group logo URL, falling back to the default logo */
export function useGroupLogo(): string | null {
  const { logoUrl } = useTheme();
  return logoUrl ?? null;
}
